import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { ConnectError, Code } from "@connectrpc/connect";
import { createClient, fieldFromError } from "@/lib/connect";
import { CampaignService } from "@gen/tracker/v1/campaign_connect";
import {
  Campaign,
  CampaignCost,
  CampaignGroup,
  CampaignListItem,
  CampaignStatus,
  RenderAdLinkResponse,
  type OwnedDomain,
} from "@gen/tracker/v1/campaign_pb";
import { DateRange, Pagination } from "@gen/tracker/v1/common_pb";
import { fetchAll, idChunks } from "@/lib/services/catalog";

const client = createClient(CampaignService);

export const campaignKeys = {
  all: ["campaigns"] as const,
  lists: ["campaigns", "list"] as const,
  list: (params: ListCampaignsParams) => [...campaignKeys.all, "list", params] as const,
  detail: (id: string) => [...campaignKeys.all, "detail", id] as const,
};

export interface ListCampaignsParams {
  search?: string;
  /** Any of these traffic sources. */
  trafficSourceIds?: string[];
  statuses?: CampaignStatus[];
  /** Keycloak subs of the owners (the «Баер» filter). */
  ownerIds?: string[];
  /** «Мои»: the caller's own, as the server knows him — never his sub named. */
  mine?: boolean;
  /** ISO-3166 alpha-2, matched against the countries of the campaigns' offers. */
  countries?: string[];
  /** Campaign groups (decision 04); "none" = campaigns in no group. */
  groupIds?: string[];
  /** These campaigns only — a screen about one campaign reads its row. */
  ids?: string[];
  /** One page; the API's default is 50 and its ceiling 500. */
  limit?: number;
  offset?: number;
  // statsRange makes each item carry `stats` — every metric of the reporting
  // schema over the period, from the one reader. When omitted `stats` is
  // empty. Format: ISO-8601 YYYY-MM-DD.
  statsRange?: { from: string; to: string };
}

async function listCampaignsPage(params: ListCampaignsParams, offset: number, limit: number) {
  const res = await client.listCampaigns({
    search: params.search ?? "",
    trafficSourceIds: params.trafficSourceIds ?? [],
    statuses: params.statuses ?? [],
    ownerIds: params.ownerIds ?? [],
    mine: params.mine ?? false,
    countries: params.countries ?? [],
    groupIds: params.groupIds ?? [],
    ids: params.ids ?? [],
    tagIds: [],
    pagination: new Pagination({ limit, offset }),
    statsRange: params.statsRange
      ? new DateRange({ from: params.statsRange.from, to: params.statsRange.to })
      : undefined,
  });
  return { items: res.items, total: res.pagination?.total ?? res.items.length };
}

export function useListCampaigns(
  params: ListCampaignsParams = {},
  options?: Partial<UseQueryOptions<{ items: CampaignListItem[]; total: number }>>,
) {
  return useQuery({
    queryKey: campaignKeys.list(params),
    queryFn: () => listCampaignsPage(params, params.offset ?? 0, params.limit ?? 50),
    ...options,
  });
}

/**
 * EVERY campaign that matches, page after page — for the screens that rank
 * campaigns by their numbers (the list, «Сегодня»): a ranking over the first
 * page of campaigns is a ranking of the wrong campaigns.
 */
export function useAllCampaigns(
  params: Omit<ListCampaignsParams, "limit" | "offset"> = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...campaignKeys.all, "list", "all", params] as const,
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    // Reports wait on these ids before they ask for numbers: a reopened
    // report reuses them instead of listing every campaign again.
    staleTime: 60_000,
    queryFn: () => fetchAll((offset, limit) => listCampaignsPage(params, offset, limit)),
  });
}

/** The campaigns with these ids, keyed by campaign id. */
export function useCampaignsByIds(ids: Iterable<string>, enabled = true) {
  const chunks = idChunks(ids);
  return useQuery({
    queryKey: [...campaignKeys.all, "by-ids", chunks] as const,
    enabled: enabled && chunks.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    queryFn: async (): Promise<Map<string, CampaignListItem>> => {
      const parts = await Promise.all(chunks.map((chunk) => listCampaignsPage({ ids: chunk }, 0, chunk.length)));
      return new Map(parts.flatMap((p) => p.items).flatMap((i) => (i.campaign ? [[i.campaign.id, i] as const] : [])));
    },
  });
}

/**
 * How many campaigns the rail's «Кампании» row counts: everything not in the
 * archive. One row asked for; the total rides in the pagination meta.
 */
export function useCampaignCount({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...campaignKeys.all, "count"] as const,
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await client.listCampaigns({
        statuses: [CampaignStatus.DRAFT, CampaignStatus.ACTIVE, CampaignStatus.PAUSED],
        pagination: new Pagination({ limit: 1, offset: 0 }),
      });
      return res.pagination?.total ?? res.items.length;
    },
  });
}

export function useGetCampaign(id: string | undefined) {
  return useQuery({
    queryKey: campaignKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      if (!id) return undefined;
      const res = await client.getCampaign({ id });
      return res.campaign;
    },
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    /** takeFqdn: the owner's domain (ListFreeDomains) the person picked for the campaign. */
    mutationFn: async ({ campaign, takeFqdn }: { campaign: Campaign; takeFqdn?: string }) => {
      const res = await client.createCampaign({ campaign, takeFqdn: takeFqdn ?? "" });
      return res.campaign!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignKeys.all });
    },
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaign, takeFqdn }: { campaign: Campaign; takeFqdn?: string }) => {
      const res = await client.updateCampaign({ campaign, expectedRevision: campaign.revision, takeFqdn: takeFqdn ?? "" });
      return res.campaign!;
    },
    onSuccess: (campaign) => {
      void qc.invalidateQueries({ queryKey: campaignKeys.all });
      qc.setQueryData(campaignKeys.detail(campaign.id), campaign);
    },
  });
}

export function useDuplicateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, newName }: { id: string; newName?: string }) => {
      const res = await client.duplicateCampaign({ id, newName: newName ?? "" });
      return res.campaign!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignKeys.all });
    },
  });
}

export function useArchiveCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.archiveCampaign({ id });
      return id;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: campaignKeys.all });
      // Lists only: a detail or a by-ids map under the same root has no
      // `items` to filter.
      const snapshots = qc.getQueriesData<{ items: CampaignListItem[]; total: number }>({
        queryKey: campaignKeys.lists,
      });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData(key, {
          ...data,
          items: data.items.filter((it) => it.campaign?.id !== id),
          total: Math.max(0, data.total - 1),
        });
      }
      return { snapshots };
    },
    onError: (_err, _id, ctx) => {
      if (!ctx?.snapshots) return;
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: campaignKeys.all });
    },
  });
}

export function usePauseCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.pauseCampaign({ id });
      return res.campaign!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignKeys.all });
    },
  });
}

export function useActivateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.activateCampaign({ id });
      return res.campaign!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignKeys.all });
    },
  });
}

// --- the campaign card ------------------------------------------------------

/**
 * The card's header and KPI strip: the campaign as the list sees it — names
 * of its source, group, offers, landers, domain and owner — with every metric
 * of the reporting schema over the Top period and the comparison window. One
 * ListCampaigns for one id: the card and the list cannot disagree on a number
 * because they are the same row from the same reader.
 */
export function useCampaignCard(
  id: string | undefined,
  range: { from: string; to: string; tz: string },
  compare: { from: string; to: string } | undefined,
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: [...campaignKeys.all, "card", id ?? "", range, compare] as const,
    enabled: !!id,
    refetchInterval: options.refetchInterval,
    queryFn: async () => {
      const res = await client.listCampaigns({
        ids: [id!],
        pagination: new Pagination({ limit: 1, offset: 0 }),
        statsRange: new DateRange({ from: range.from, to: range.to, timezone: range.tz }),
        compareRange: compare ? new DateRange({ from: compare.from, to: compare.to, timezone: range.tz }) : undefined,
        // An archived campaign still has a card.
        statuses: [CampaignStatus.DRAFT, CampaignStatus.ACTIVE, CampaignStatus.PAUSED, CampaignStatus.ARCHIVED],
      });
      return res.items[0];
    },
  });
}

export const campaignGroupKeys = ["campaign-groups"] as const;

export function useListCampaignGroups() {
  return useQuery({
    queryKey: campaignGroupKeys,
    staleTime: 60_000,
    queryFn: async (): Promise<CampaignGroup[]> => (await client.listCampaignGroups({})).groups,
  });
}

export function useCreateCampaignGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => (await client.createCampaignGroup({ name })).group!,
    onSuccess: () => void qc.invalidateQueries({ queryKey: campaignGroupKeys }),
  });
}

/** Every domain the caller owns at the domain provider (attached or not), the served ones first. */
export function useOwnedDomains(enabled = true) {
  return useQuery({
    queryKey: [...campaignKeys.all, "owned-domains"] as const,
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<OwnedDomain[]> => (await client.listFreeDomains({ limit: 500 })).domains,
  });
}

/** The ad link the source's ad account asks for, rendered by the server. */
/**
 * What «Ссылки и постбеки» can show for a campaign's ad link:
 *   - `link` with nothing missing — the link to paste;
 *   - `link` + `missing` — an answer the source asks for is not saved (a twin
 *     carries its own pixel in the ad it already runs), so the address and its
 *     parameters are shown as a check, with the reason;
 *   - `noSource` — no traffic source is chosen, so there is no link to render.
 */
export interface AdLinkView {
  link?: RenderAdLinkResponse;
  missing?: string;
  noSource?: boolean;
}

export function useRenderAdLink(campaignId: string | undefined) {
  return useQuery({
    queryKey: [...campaignKeys.all, "ad-link", campaignId ?? ""] as const,
    enabled: !!campaignId,
    queryFn: async (): Promise<AdLinkView> => {
      try {
        return { link: await client.renderAdLink({ campaignId: campaignId! }) };
      } catch (err) {
        const field = fieldFromError(err);
        if (field === "source") return { noSource: true };
        if (!(err instanceof ConnectError) || err.code !== Code.InvalidArgument || !field) throw err;
        // The server words it «<field>: <why>»; the person reads the why.
        const missing = err.rawMessage.startsWith(`${field}: `) ? err.rawMessage.slice(field.length + 2) : err.rawMessage;
        return { link: await client.renderAdLink({ campaignId: campaignId!, preview: true }), missing };
      }
    },
  });
}

export const campaignCostKeys = (campaignId: string) => [...campaignKeys.all, "costs", campaignId] as const;

export function useListCampaignCosts(campaignId: string | undefined) {
  return useQuery({
    queryKey: campaignCostKeys(campaignId ?? ""),
    enabled: !!campaignId,
    queryFn: async (): Promise<CampaignCost[]> => (await client.listCampaignCosts({ campaignId: campaignId! })).costs,
  });
}

export function useAddCampaignCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { campaignId: string; dateFrom: string; dateTo: string; amount: string; note?: string }) =>
      (await client.addCampaignCost({ ...v, note: v.note ?? "" })).cost!,
    onSuccess: (c) => void qc.invalidateQueries({ queryKey: campaignCostKeys(c.campaignId) }),
  });
}

export function useDeleteCampaignCost(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.deleteCampaignCost({ id });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: campaignCostKeys(campaignId) }),
  });
}
